import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchPersonnel} from '@/entities/personnel/api';
import {faceState} from '@/entities/personnel/face';
import type {
  AttendanceSchedule,
  PersonnelRecord,
} from '@/entities/personnel/types';
import {Avatar} from '@/shared/ui/avatar';
import {EmptyState} from '@/shared/ui/empty-state';
import {Field, FieldGroup} from '@/shared/ui/field';
import {Skeleton} from '@/shared/ui/skeleton';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing} from '@/shared/ui/theme';
import {activeWeekdays, formatDayMinutes} from '@/shared/lib/format';

/**
 * A person, read-only. The face comes from `avatar_base64` on the record this card
 * already fetched — not through `PersonAvatar`, which every LIST uses: a list has only
 * an id and must ask for the image separately, while this card holds the whole record
 * and asking twice would be a second round trip for bytes already in hand. Everything
 * here is display: the Home context cannot edit people (they are owned by the terminal
 * sync).
 *
 * The head is a portrait block — face, name, role, and the one status that matters
 * about a person in an access system (is their face enrolled). The facts sit under it
 * in one grouped card instead of four floating label/value pairs.
 */
const AVATAR_STALE_MS = 5 * 60_000;

export type PersonCardProps = {
  companyId: string;
  recordId: string;
};

const styles = StyleSheet.create({
  root: {gap: spacing(5)},
  head: {alignItems: 'center', gap: spacing(3), paddingVertical: spacing(3)},
  headText: {alignItems: 'center', gap: spacing(1)},
  centered: {textAlign: 'center'},
  skeleton: {alignItems: 'center', gap: spacing(3), paddingVertical: spacing(6)},
});

/** "Пн Вт Ср Чт Пт · 09:00–18:00", or a translated "not set" when unconfigured. */
function scheduleSummary(
  schedule: AttendanceSchedule | null | undefined,
  weekdayLabel: (i: number) => string,
  notSet: string,
): string {
  const start = formatDayMinutes(schedule?.start_minutes);
  const end = formatDayMinutes(schedule?.end_minutes);
  if (!schedule || start == null || end == null) {
    return notSet;
  }
  const days = activeWeekdays(schedule.workdays_mask).map(weekdayLabel).join(' ');
  const window = `${start}–${end}`;
  return days ? `${days} · ${window}` : window;
}

export function PersonCard({companyId, recordId}: PersonCardProps) {
  const {t} = useTranslation();

  const query = useQuery({
    queryKey: ['personnel', companyId, recordId],
    queryFn: () => fetchPersonnel(companyId, recordId),
    staleTime: AVATAR_STALE_MS,
  });

  if (query.isPending) {
    return (
      <View style={styles.root}>
        <View style={styles.skeleton}>
          <Skeleton width={96} height={96} radius={48} />
          <Skeleton width={160} height={18} />
          <Skeleton width={110} height={12} />
        </View>
        <Skeleton height={210} radius={radii.lg} />
      </View>
    );
  }

  if (query.isError) {
    return (
      <EmptyState
        tone="error"
        title={t('home.person.notFound')}
        actionLabel={t('actions.retry')}
        onAction={() => {
          void query.refetch();
        }}
      />
    );
  }

  const person: PersonnelRecord = query.data;
  // Three states, and the third is not decoration: no answer means "nobody has
  // reported", which is not the same claim as "no face".
  const face = faceState(person);
  const sourceKey = person.effective_attendance_schedule_source;
  const scheduleSource =
    sourceKey === 'personnel' || sourceKey === 'department'
      ? t(`home.person.scheduleSource.${sourceKey}`)
      : null;
  const schedule = scheduleSummary(
    person.effective_attendance_schedule,
    i => t(`home.person.weekday.${i}`),
    t('home.person.scheduleNone'),
  );

  return (
    <View style={styles.root}>
      <View style={styles.head}>
        <Avatar name={person.full_name} base64={person.avatar_base64} size={104} />
        <View style={styles.headText}>
          <AppText variant="title" style={styles.centered}>
            {person.full_name}
          </AppText>
          {person.position ? (
            <AppText variant="caption" style={styles.centered}>
              {person.position}
            </AppText>
          ) : null}
        </View>
        <StatusPill
          // `unknown` is neutral grey, never the warning amber: missing data is
          // not a problem to act on, and colouring it as one sends the owner
          // hunting for a fault that does not exist.
          tone={face === 'yes' ? 'granted' : face === 'no' ? 'pending' : 'neutral'}
          label={t(
            face === 'yes'
              ? 'home.person.faceEnrolled'
              : face === 'no'
              ? 'home.person.faceMissing'
              : 'home.person.faceUnknown',
          )}
        />
      </View>

      <FieldGroup>
        <Field
          label={t('home.person.departments')}
          value={person.department_names.join(', ')}
        />
        <Field
          label={t('home.person.devices')}
          value={person.device_names.join(', ')}
        />
        <Field
          label={t('home.person.schedule')}
          value={schedule}
          note={scheduleSource}
        />
        <Field label={t('home.person.nfc')} value={person.nfc_uid} mono />
      </FieldGroup>
    </View>
  );
}
